<template>
  <div id="styled-tags-input">
    <vue-tags-input
      v-model="currentInput"
      :add-only-from-autocomplete="addOnlyFromAutocomplete"
      :autocomplete-items="filteredAutoCompleteItems"
      :autocomplete-min-length="0"
      :class="{
        busy,
        error: isError,
      }"
      :disabled="busy || disabled"
      :placeholder="t('styledTagsInput.addTag')"
      :separators="separators"
      :tags="tags"
      @tags-changed="handleTagsChanged"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch, onBeforeUnmount, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import VueTagsInput from '@sipec/vue3-tags-input';
  import { transformStringToRegex } from 'utils';
  import { DEFAULT_METADATA_VALUES_SEPARATOR } from 'types';
  import {
    ITag,
    IAutocompleteItem,
    ITagClasses,
  } from './StyledTagsInput.types';

  const { t } = useI18n();

  const emit = defineEmits<{
    (e: 'tags-changed', payload?: string[]): void;
    (e: 'update:modelValue', payload?: string[]): void;
    (e: 'before-destroy'): void;
  }>();

  const {
    busy = false,
    addOnlyFromAutocomplete = false,
    isError = false,
    separators = [DEFAULT_METADATA_VALUES_SEPARATOR],
    disabled = false,
    ...props
  } = defineProps<{
    autocompleteItems?: string[];
    busy?: boolean;
    addOnlyFromAutocomplete?: boolean;
    modelValue: string[];
    isError?: boolean;
    separators?: string[];
    tagClasses?: ITagClasses;
    disabled?: boolean;
  }>();

  const tags = ref<ITag[]>([]);
  const currentInput = ref('');
  const filteredAutoCompleteItems = ref<IAutocompleteItem[]>([]);

  function setCurrentTags() {
    if (props.modelValue.length === 1 && props.modelValue[0] === '') {
      return;
    }

    tags.value = props.modelValue.map((tag) => {
      const tagObject: ITag = {
        text: tag,
      };

      if (props.tagClasses?.[tag]) {
        tagObject.classes = props.tagClasses[tag];
      }

      return tagObject;
    });
  }

  function handleTagsChanged(newTags: ITag[]) {
    tags.value = newTags;
    const tagsText = tags.value.map((tag) => tag.text);

    emit('tags-changed', tagsText);
    /** Supporting v-model */
    emit('update:modelValue', tagsText);
  }

  function transformAutoCompleteItemsToObjects(
    autocompleteItems?: string[],
  ): IAutocompleteItem[] {
    if (!autocompleteItems) return [];
    return autocompleteItems.map((item) => ({ text: item }));
  }
  onMounted(() => {
    setCurrentTags();
    filteredAutoCompleteItems.value = transformAutoCompleteItemsToObjects(
      props.autocompleteItems,
    );
  });

  watch(() => props.modelValue, setCurrentTags);
  watch(() => props.tagClasses, setCurrentTags);
  watch(currentInput, () => {
    if (!currentInput.value) {
      filteredAutoCompleteItems.value = transformAutoCompleteItemsToObjects(
        props.autocompleteItems,
      );
    }

    const searchRegex = transformStringToRegex(currentInput.value, 'i');

    filteredAutoCompleteItems.value = transformAutoCompleteItemsToObjects(
      props.autocompleteItems?.filter((item) => searchRegex.test(item)),
    );
  });
  watch(
    () => props.autocompleteItems,
    () => {
      filteredAutoCompleteItems.value = transformAutoCompleteItemsToObjects(
        props.autocompleteItems,
      );
    },
  );

  onBeforeUnmount(() => {
    emit('before-destroy');
  });
</script>

<style lang="scss" scoped>
  :deep(.vue-tags-input) {
    .success {
      background-color: $success-color;
    }

    .warning {
      background-color: $warning-color;
    }

    .danger {
      background-color: $danger-color;
    }

    &.busy {
      .ti-tags {
        @include pulsating-animation;
      }
    }

    &.error {
      .ti-input {
        border-color: red;
      }
    }
  }
</style>
